import React, { useState, useEffect, useMemo } from "react";
import BusinessUsersTable from "./BusinessUsersTable";

const API_BASE = "https://api.vyapakprint.com/api";

const BusinessUsers = () => {
  const [businessUsers, setBusinessUsers] = useState([]);
  const [businessPendingRequests, setBusinessPendingRequests] = useState([]);
  const [businessShowDetails, setBusinessShowDetails] = useState(null);

  const [userType, setUserType] = useState("");
  const [userTypes, setUserTypes] = useState([]);
  const [showUsers, setShowUsers] = useState(false);

  const [searchPending, setSearchPending] = useState("");
  const [searchActive, setSearchActive] = useState("");

  const [selectedUserTypes, setSelectedUserTypes] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [loadingApproveId, setLoadingApproveId] = useState(null);
  const [addingUserType, setAddingUserType] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const [stats, setStats] = useState({
    totalDistributions: 0,
    activeDistributions: 0,
    inactiveDistributions: 0,
    blockedDistributions: 0,
    rejectedDistributions: 0,
    totalWalletBalance: 0,
    averageWalletBalance: 0,
    totalCreditTransactions: 0,
    totalCreditAmount: 0,
    totalDebitTransactions: 0,
    totalDebitAmount: 0,
  });

  const normalizeStatus = (status) => String(status || "").toLowerCase();

  const getUserTypeLabel = (userTypeValue) => {
    if (!userTypeValue) return "N/A";

    const found = userTypes.find(
      (type) =>
        type?._id === userTypeValue ||
        type?.type === userTypeValue ||
        String(type?._id) === String(userTypeValue) ||
        String(type?._id) === String(userTypeValue?._id),
    );

    if (typeof userTypeValue === "object" && userTypeValue?.type) {
      return userTypeValue.type;
    }

    return found?.type || userTypeValue?.type || userTypeValue || "N/A";
  };

  const matchesSearch = (term) => (u) => {
    if (!term.trim()) return true;
    const t = term.toLowerCase();

    return (
      (u.businessName || "").toLowerCase().includes(t) ||
      (u.email || "").toLowerCase().includes(t) ||
      (u.contactPerson || "").toLowerCase().includes(t) ||
      String(u.mobile || "")
        .toLowerCase()
        .includes(t) ||
      String(u.city || "")
        .toLowerCase()
        .includes(t) ||
      String(u.state || "")
        .toLowerCase()
        .includes(t) ||
      String(getUserTypeLabel(u.userType) || "")
        .toLowerCase()
        .includes(t)
    );
  };

  const filteredPending = useMemo(
    () => businessPendingRequests.filter(matchesSearch(searchPending)),
    [businessPendingRequests, searchPending, userTypes],
  );

  const filteredActive = useMemo(
    () => businessUsers.filter(matchesSearch(searchActive)),
    [businessUsers, searchActive, userTypes],
  );

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `₹${num.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/distribution/`);
      if (!response.ok) throw new Error("Failed to fetch business users");

      const result = await response.json();
      const data = Array.isArray(result)
        ? result
        : Array.isArray(result?.distributions)
          ? result.distributions
          : [];

      const activeUsers = data.filter(
        (user) => normalizeStatus(user.status) === "active",
      );

      const pendingRequests = data.filter(
        (user) => normalizeStatus(user.status) !== "active",
      );

      setBusinessUsers(activeUsers);
      setBusinessPendingRequests(pendingRequests);

      const nextSelectedUserTypes = {};
      const nextSelectedStatuses = {};

      data.forEach((user) => {
        nextSelectedUserTypes[user._id] =
          user?.userType?._id || user?.userType || "";
        nextSelectedStatuses[user._id] =
          normalizeStatus(user.status) || "inactive";
      });

      setSelectedUserTypes(nextSelectedUserTypes);
      setSelectedStatuses(nextSelectedStatuses);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/distribution/stats/summary`);
      if (!response.ok) throw new Error("Failed to fetch stats");

      const data = await response.json();
      setStats(data?.stats || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchUserTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/userType/getUserTypes`);
      if (!response.ok) throw new Error("Failed to fetch user types");

      const data = await response.json();
      setUserTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUserTypes();
    fetchStats();
  }, []);

  const refreshAll = async () => {
    await Promise.all([fetchUsers(), fetchStats()]);
  };

  const addUserType = async () => {
    if (userType.trim() === "") {
      alert("Please enter a user type");
      return;
    }

    try {
      setAddingUserType(true);

      const response = await fetch(`${API_BASE}/userType/addUserType`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type: userType.trim() }),
      });

      if (!response.ok) {
        throw new Error("Failed to add user type");
      }

      const newUserType = await response.json();
      setUserTypes((prev) => [...prev, newUserType]);
      setUserType("");
    } catch (error) {
      console.error("Error adding user type:", error);
      alert("Failed to add user type");
    } finally {
      setAddingUserType(false);
    }
  };

  const deleteUserType = async (userTypeId) => {
    const ok = window.confirm(
      "Are you sure you want to delete this user type?",
    );
    if (!ok) return;

    try {
      const response = await fetch(
        `${API_BASE}/userType/deleteUserType/${userTypeId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete user type");
      }

      setUserTypes((prev) => prev.filter((type) => type._id !== userTypeId));
    } catch (error) {
      console.error("Error deleting user type:", error);
      alert("Failed to delete user type");
    }
  };

  const handleShowUsers = () => {
    setShowUsers((prev) => !prev);
  };

  const toggleBusinessDetails = (userId) => {
    setBusinessShowDetails((prev) => (prev === userId ? null : userId));
  };

  const handleUserTypeChange = (userId, value) => {
    setSelectedUserTypes((prev) => ({
      ...prev,
      [userId]: value,
    }));
  };

  const handleStatusChange = (userId, value) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [userId]: value,
    }));
  };

  const approveBusinessRequest = async (userId) => {
    const selectedUserType = selectedUserTypes[userId];

    if (!selectedUserType) {
      alert("Please select a user type first");
      return;
    }

    try {
      setLoadingApproveId(userId);

      const response = await fetch(
        `${API_BASE}/distribution/${userId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "active",
            userType: selectedUserType,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to approve distributor");
      }

      await refreshAll();

      if (businessShowDetails === userId) {
        setBusinessShowDetails(null);
      }

      alert(result?.message || "Business request approved successfully");
    } catch (error) {
      console.error("Error approving request:", error);
      alert(error.message || "Failed to approve request");
    } finally {
      setLoadingApproveId(null);
    }
  };

  const updateUserMeta = async (user) => {
    const selectedUserType = selectedUserTypes[user._id] || "";
    const selectedStatus = selectedStatuses[user._id] || "inactive";

    if (!selectedUserType) {
      alert("Please select user type");
      return;
    }

    try {
      setUpdatingUserId(user._id);

      const response = await fetch(
        `${API_BASE}/distribution/${user._id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: selectedStatus,
            userType: selectedUserType,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to update user");
      }

      await refreshAll();

      alert(result?.message || "User details updated successfully");
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.message || "Failed to update user details");
    } finally {
      setUpdatingUserId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Total Distributors</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {stats.totalDistributions || 0}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {stats.activeDistributions || 0}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Inactive / Rejected / Blocked</p>
          <p className="text-2xl font-bold text-red-600 mt-1">
            {(stats.inactiveDistributions || 0) +
              (stats.rejectedDistributions || 0) +
              (stats.blockedDistributions || 0)}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Total Wallet Balance</p>
          <p className="text-2xl font-bold text-indigo-600 mt-1">
            {formatCurrency(stats.totalWalletBalance || 0)}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Avg Wallet</p>
          <p className="text-xl font-bold text-gray-800 mt-1">
            {formatCurrency(stats.averageWalletBalance || 0)}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Credit Txns</p>
          <p className="text-xl font-bold text-green-600 mt-1">
            {stats.totalCreditTransactions || 0}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {formatCurrency(stats.totalCreditAmount || 0)}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Debit Txns</p>
          <p className="text-xl font-bold text-red-600 mt-1">
            {stats.totalDebitTransactions || 0}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {formatCurrency(stats.totalDebitAmount || 0)}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-5 border">
          <p className="text-sm text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">
            {stats.rejectedDistributions || 0}
          </p>
        </div>
      </div>

      {/* User type manager */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4">Add User Type</h2>

        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            placeholder="Enter User Type"
            className="border p-3 rounded w-full"
          />

          <button
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg disabled:opacity-50"
            onClick={addUserType}
            disabled={addingUserType}
          >
            {addingUserType ? "Adding..." : "Add User Type"}
          </button>

          <button
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg"
            onClick={handleShowUsers}
          >
            {showUsers ? "Hide Users" : "Show Users"}
          </button>
        </div>

        {showUsers && (
          <div className="mt-6">
            <h3 className="text-xl font-semibold mb-4">All User Types</h3>

            {userTypes.length === 0 ? (
              <p className="text-gray-500">No user types found.</p>
            ) : (
              <ul className="space-y-2">
                {userTypes.map((type) => (
                  <li
                    key={type._id}
                    className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border"
                  >
                    <span className="font-medium">{type.type}</span>
                    <button
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                      onClick={() => deleteUserType(type._id)}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Pending */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h2 className="text-2xl font-semibold">
            Pending Distributor Requests
          </h2>

          <input
            type="text"
            value={searchPending}
            onChange={(e) => setSearchPending(e.target.value)}
            placeholder="Search pending users..."
            className="border p-3 rounded w-full md:w-[320px]"
          />
        </div>

        {filteredPending.length > 0 ? (
          <div className="space-y-4">
            {filteredPending.map((user) => {
              const selectedType = selectedUserTypes[user._id] || "";
              const selectedStatus = selectedStatuses[user._id] || "inactive";

              return (
                <div
                  key={user._id}
                  className="p-4 bg-gray-50 rounded-xl border shadow-sm"
                >
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-bold text-lg text-gray-800">
                        {user.businessName || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">
                        {user.email || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">
                        {user.contactPerson || "N/A"} • {user.mobile || "N/A"}
                      </p>
                    </div>

                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <select
                          className="border p-2 rounded min-w-[220px]"
                          onChange={(e) =>
                            handleUserTypeChange(user._id, e.target.value)
                          }
                          value={selectedType}
                        >
                          <option value="">Select User Type</option>
                          {userTypes.map((type) => (
                            <option key={type._id} value={type._id}>
                              {type.type}
                            </option>
                          ))}
                        </select>

                        <select
                          className="border p-2 rounded min-w-[160px]"
                          value={selectedStatus}
                          onChange={(e) =>
                            handleStatusChange(user._id, e.target.value)
                          }
                        >
                          <option value="inactive">Inactive</option>
                          <option value="active">Active</option>
                          <option value="rejected">Rejected</option>
                          <option value="blocked">Blocked</option>
                        </select>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <button
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                          onClick={() => toggleBusinessDetails(user._id)}
                        >
                          {businessShowDetails === user._id
                            ? "Hide Details"
                            : "Show Details"}
                        </button>

                        <button
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                          onClick={() => approveBusinessRequest(user._id)}
                          disabled={loadingApproveId === user._id}
                        >
                          {loadingApproveId === user._id
                            ? "Approving..."
                            : "Approve"}
                        </button>

                        <button
                          className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                          onClick={() => updateUserMeta(user)}
                          disabled={updatingUserId === user._id}
                        >
                          {updatingUserId === user._id
                            ? "Updating..."
                            : "Update User"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {businessShowDetails === user._id && (
                    <div className="mt-4 bg-white rounded-xl border p-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Business Name</p>
                          <p className="font-semibold">
                            {user.businessName || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">
                            Contact Person
                          </p>
                          <p className="font-semibold">
                            {user.contactPerson || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-semibold">{user.email || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Mobile</p>
                          <p className="font-semibold">
                            {user.mobile || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Country</p>
                          <p className="font-semibold">
                            {user.country || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">State</p>
                          <p className="font-semibold">{user.state || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">City</p>
                          <p className="font-semibold">{user.city || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Pin Code</p>
                          <p className="font-semibold">
                            {user.pinCode || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">GST Number</p>
                          <p className="font-semibold">
                            {user.gstNumber || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">GSTIN Scheme</p>
                          <p className="font-semibold">
                            {user.gstinScheme || "N/A"}
                          </p>
                        </div>

                        <div className="md:col-span-2 xl:col-span-2">
                          <p className="text-xs text-gray-500">
                            Shipping Address
                          </p>
                          <p className="font-semibold">
                            {user.shippingAddress || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">User Type</p>
                          <p className="font-semibold">
                            {getUserTypeLabel(user.userType)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <p className="font-semibold capitalize">
                            {user.status || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Wallet</p>
                          <p className="font-semibold">
                            {formatCurrency(user.wallet || 0)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Created At</p>
                          <p className="font-semibold">
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleString()
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500">No pending requests.</p>
        )}
      </div>

      {/* Active */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h2 className="text-2xl font-semibold">Active Business Users</h2>

          <input
            type="text"
            value={searchActive}
            onChange={(e) => setSearchActive(e.target.value)}
            placeholder="Search active users..."
            className="border p-3 rounded w-full md:w-[320px]"
          />
        </div>

        {filteredActive.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredActive.map((user) => {
              const selectedType = selectedUserTypes[user._id] || "";
              const selectedStatus = selectedStatuses[user._id] || "active";

              return (
                <div
                  key={user._id}
                  className="bg-gray-50 border rounded-xl p-4 shadow-sm"
                >
                  <p className="font-bold text-gray-800">
                    {user.businessName || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Username: {user.email || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600 ">
                    Password: {user.copypassword || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    Contact: {user.contactPerson || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    Mobile: {user.mobile || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    Wallet: {formatCurrency(user.wallet || 0)}
                  </p>

                  <p className="text-sm mt-3">
                    <span className="text-gray-500">User Type:</span>{" "}
                    <span className="text-indigo-600 font-semibold">
                      {getUserTypeLabel(user.userType)}
                    </span>
                  </p>

                  <p className="text-sm mt-1">
                    <span className="text-gray-500">Status:</span>{" "}
                    <span className="text-green-600 font-semibold capitalize">
                      {user.status || "active"}
                    </span>
                  </p>

                  <div className="mt-4 space-y-3">
                    <select
                      className="border p-2 rounded w-full"
                      value={selectedType}
                      onChange={(e) =>
                        handleUserTypeChange(user._id, e.target.value)
                      }
                    >
                      <option value="">Select User Type</option>
                      {userTypes.map((type) => (
                        <option key={type._id} value={type._id}>
                          {type.type}
                        </option>
                      ))}
                    </select>

                    <select
                      className="border p-2 rounded w-full"
                      value={selectedStatus}
                      onChange={(e) =>
                        handleStatusChange(user._id, e.target.value)
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="rejected">Rejected</option>
                      <option value="blocked">Blocked</option>
                    </select>

                    <button
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                      onClick={() => updateUserMeta(user)}
                      disabled={updatingUserId === user._id}
                    >
                      {updatingUserId === user._id
                        ? "Updating..."
                        : "Update User Type / Status"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500">No active users found.</p>
        )}
      </div>

      <BusinessUsersTable />
    </div>
  );
};

export default BusinessUsers;
